import React, { useState, useEffect, useRef } from 'react';

import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { useResizeObserver } from 'usehooks-ts';

import { Input, Table, DatePicker, notification, Tooltip, Select, Button, Modal } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';

import IBaseComponentProps from '@components/base-component-props';

import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';

import { exception } from '@extensions/notification';

import { delayAction, getDateTimeLocal, getEnumList } from '@extensions/utils';

import { userLoaded, setFilter } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { INotification } from '@entities/notification';
import { INotificationFilter } from '@entities/notification-filter';

import { NotificationStatus, enumLabel as NotificationStatusLabel } from '@enums/notification-status';
import { NotificationMessageType } from '@enums/notification-message-type';
import { IEnumItem } from '@enums/enum-item';

dayjs.extend(utc);

const filterContext: string = 'TelegramNotifications';

const TelegramMessages = (props: IBaseComponentProps) => {
    const { serverFetch } = props;

    const { TextArea } = Input;

    const initFilter: INotificationFilter = {
        messageType: NotificationMessageType.Telegram,
        personalOnly: false,
        createdFrom: dayjs().add(-2, 'day').utc(true).set('hour', 0).set('minute', 0).set('second', 0),
    };

    const { RangePicker } = DatePicker;

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const [api, contextHolder] = notification.useNotification();

    const d = useAppDispatch();
    const filter = useAppSelector<INotificationFilter>((s) => s.filters[filterContext]);

    const [entities, setEntities] = useState<Array<INotification>>([]);
    const [statuses] = useState<Array<IEnumItem>>(getEnumList(NotificationStatus, NotificationStatusLabel));
    const [loading, setLoading] = useState<boolean>(false);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);

    const [openModal, setOpenModal] = useState<boolean>();
    const [currentNotification, setCurrentNotification] = useState<INotification | undefined>();

    const [showFilter, setShowFilter] = useState<boolean>(true);

    useEffect(() => {
        if (!refreshRequired) return;

        setRefreshRequired(false);

        setEntities([]);

        setLoading(true);

        serverFetch('notifications', { method: 'GET', queryParams: filter })
            .then((data: any) => {
                setEntities(data);
                setLoading(false);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения сообщений', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));

            return;
        }
    }, [filter]);

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => setRefreshRequired(true),
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            key='searchText'
                            placeholder='Логин / ФИО / Телефон / Контрагент / Сообщение'
                            value={filter?.searchText}
                            style={{ width: 320 }}
                            onChange={(data) => {
                                d(setFilter({ ...filter, searchText: data.target.value }, filterContext));
                            }}
                        />,
                        <RangePicker
                            style={{ width: 260 }}
                            allowEmpty={[true, true]}
                            key='created'
                            format='DD.MM.YYYY'
                            placeholder={['Отправлено с', 'до']}
                            cellRender={(current) => {
                                return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                            }}
                            value={[
                                filter?.createdFrom ? dayjs(filter?.createdFrom) : null,
                                filter?.createdTo ? dayjs(filter?.createdTo) : null,
                            ]}
                            onChange={(value) => {
                                if (!value) {
                                    d(
                                        setFilter(
                                            {
                                                ...filter,
                                                createdFrom: undefined,
                                                createdTo: undefined,
                                            },
                                            filterContext,
                                        ),
                                    );
                                    return;
                                }

                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            createdFrom: value[0]
                                                ? dayjs(value[0]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                            createdTo: value[1]
                                                ? dayjs(value[1]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                        },
                                        filterContext,
                                    ),
                                );
                            }}
                        />,
                        <Select
                            key='status'
                            placeholder='Статус'
                            value={filter?.statuses}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, statuses: value }, filterContext))}
                            options={statuses.map((s) => {
                                return { value: s.value, label: s.label };
                            })}
                        />,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<INotification> = [
            {
                title: 'Логин',
                dataIndex: 'userLogin',
                width: 150,
            },
            {
                title: 'ФИО',
                dataIndex: 'userFullName',
                width: 300,
            },
            {
                title: 'Телефон',
                dataIndex: 'userPhone',
                width: 130,
            },
            {
                title: 'Контрагент',
                dataIndex: 'legalName',
                width: 350,
            },

            {
                title: 'Сообщение',
                width: 300,
                render: (_: any, record: INotification) => {
                    const dots = record.body && record.body.length > 38 ? '...' : '';
                    return (
                        <Tooltip title='Открыть подробнее...'>
                            <Button
                                style={{ padding: 0, height: 'fit-content' }}
                                variant='link'
                                color='blue'
                                onClick={() => {
                                    setCurrentNotification(record);
                                    setOpenModal(true);
                                }}
                            >
                                {record.body?.substring(0, 38)}
                                {dots}
                            </Button>
                        </Tooltip>
                    );
                },
            },
            {
                title: 'Статус',
                width: 140,
                render: (_, record) => {
                    return NotificationStatusLabel(record.status);
                },
            },
            {
                title: 'Отправлено',
                width: 130,
                render: (_, record) => {
                    return getDateTimeLocal(record.sentOn);
                },
            },
            {
                title: 'Отправитель',
                dataIndex: 'createdByName',
                width: 250,
            },

            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={entities}
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                pagination={false}
                virtual={true}
                scroll={{ y: height ?? 0 + (showFilter ? 0 : 58) }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 195px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            <Modal
                title={`Сообщение для ${currentNotification?.userFullName} от ${getDateTimeLocal(currentNotification?.createdOn)}`}
                width={600}
                open={openModal && !!currentNotification}
                okButtonProps={{
                    style: {
                        display: 'none',
                    },
                }}
                cancelText='Закрыть'
                onCancel={() => {
                    setCurrentNotification(undefined);
                    setOpenModal(false);
                }}
                closable={false}
            >
                <TextArea readOnly={true} rows={10} value={currentNotification?.body} />
            </Modal>

            {contextHolder}
        </div>
    );
};

export default TelegramMessages;
